import React, { useState, useEffect } from 'react';
import { ProjectCard } from '../components/projects/ProjectCard';
import { Button } from '../components/Button';
import { projectService, Project, ProjectFilters } from '../services/projectService';
import { Link } from 'react-router-dom';
import { useDebounce } from '../hooks/useDebounce';

// Sidebar categories
const sidebarCategories = [
  { id: 'ai-ml', name: 'AI & ML' },
  { id: 'ecommerce', name: 'E-commerce' },
  { id: 'tools', name: 'Tools & Utilities' },
  { id: 'games', name: 'Game Projects' },
  { id: 'open-source', name: 'Open Source' },
  { id: 'content', name: 'Content & Media' },
  { id: 'saas', name: 'SaaS' },
  { id: 'other', name: 'Other' },
];

const priceRanges = [
  { id: 'all', name: 'All Prices' },
  { id: 'under-1000', name: 'Under $1,000' },
  { id: '1000-5000', name: '$1,000 - $5,000' },
  { id: '5000-10000', name: '$5,000 - $10,000' },
  { id: 'over-10000', name: 'Over $10,000' },
];

const revenueStatuses = [
  { id: 'all', name: 'All Statuses' },
  { id: 'pre-revenue', name: 'Pre-revenue' },
  { id: 'revenue', name: 'Generating Revenue' },
  { id: 'na', name: 'N/A' },
];

const EmptyState: React.FC<{ category: string }> = ({ category }) => (
  <div className="text-center py-12">
    <div className="mx-auto h-48 w-48 text-gray-400 mb-8">
      <svg className="h-full w-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1}
          d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
        />
      </svg>
    </div>
    <h3 className="text-2xl font-semibold text-gray-900 mb-2">No projects found</h3>
    <p className="text-gray-600 mb-8">
      {category === 'all' 
        ? "Looks like there aren't any projects listed yet. Be the first to list your project!"
        : `No projects found in the "${sidebarCategories.find(c => c.id === category)?.name || category}" category yet.`
      }
    </p>
    <Link to="/sell-your-project">
      <Button className="bg-primary-600 text-white hover:bg-primary-700">
        List Your Project
      </Button>
    </Link>
  </div>
);

export const BrowseProjects: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showSidebar, setShowSidebar] = useState(false);
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500); // 500ms delay

  const [filters, setFilters] = useState<ProjectFilters>({
    category: undefined,
    minPrice: undefined,
    maxPrice: undefined,
    revenueStatus: undefined,
    status: 'active',
    page: 1,
    limit: 12,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    search: undefined
  });

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const { projects: fetchedProjects, total } = await projectService.getProjects(filters);
      setProjects(fetchedProjects);
      setTotalPages(Math.ceil(total / 12));
    } catch (error) {
      console.error('Error fetching projects:', error);
      setError('Failed to load projects. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [filters]);

  // Update filters when debounced search term changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      search: debouncedSearchTerm.trim() || undefined,
      page: 1
    }));
  }, [debouncedSearchTerm]);

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setFilters(prev => ({
      ...prev,
      category: category === 'all' ? undefined : category,
      page: 1
    }));
  };

  const handlePriceRangeChange = (range: string) => {
    let minPrice: number | undefined;
    let maxPrice: number | undefined;

    switch (range) {
      case 'under-1000':
        maxPrice = 1000;
        break;
      case '1000-5000':
        minPrice = 1000;
        maxPrice = 5000;
        break;
      case '5000-10000':
        minPrice = 5000;
        maxPrice = 10000;
        break;
      case 'over-10000':
        minPrice = 10000;
        break;
    }

    setFilters(prev => ({
      ...prev,
      minPrice,
      maxPrice,
      page: 1
    }));
  };

  const handleRevenueStatusChange = (status: string) => {
    setFilters(prev => ({
      ...prev,
      revenueStatus: status === 'all' ? undefined : status as 'pre-revenue' | 'revenue' | 'na',
      page: 1
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header Section */}
      <div className="bg-white/80 backdrop-blur-sm sticky top-0 z-10 border-b border-gray-100">
        <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-8">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-accent-500 bg-clip-text text-transparent">
                Browse Projects
              </h1>
              <div className="hidden md:flex items-center gap-2">
                <button
                  onClick={() => setView('grid')}
                  className={`p-2 rounded-lg ${
                    view === 'grid'
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button
                  onClick={() => setView('list')}
                  className={`p-2 rounded-lg ${
                    view === 'list'
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <form onSubmit={(e) => e.preventDefault()} className="hidden md:block">
                <div className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search projects..."
                    className="w-64 px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all duration-200"
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
                    {searchTerm && (
                      <button
                        type="button"
                        onClick={handleClearSearch}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
              </form>
              <Button
                variant="secondary"
                onClick={() => setShowSidebar(!showSidebar)}
                className="flex items-center gap-2 bg-white shadow-sm hover:shadow transition-all duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filters
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex gap-8 py-6">
          {/* Sidebar */}
          <div className={`w-64 flex-shrink-0 ${showSidebar ? 'block' : 'hidden lg:block'}`}>
            <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-soft p-6 sticky top-24 border border-gray-100">
              <h2 className="text-lg font-bold bg-gradient-to-r from-primary-600 to-accent-500 bg-clip-text text-transparent mb-4">
                Categories
              </h2>
              <nav className="space-y-1 mb-6">
                <button
                  onClick={() => handleCategoryChange('all')}
                  className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    selectedCategory === 'all'
                      ? 'bg-primary-50 text-primary-600 shadow-sm'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  All Projects
                </button>
                {sidebarCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryChange(category.id)}
                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                      selectedCategory === category.id
                        ? 'bg-primary-50 text-primary-600 shadow-sm'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
              </nav>

              <h2 className="text-lg font-bold bg-gradient-to-r from-primary-600 to-accent-500 bg-clip-text text-transparent mb-4">
                Price Range
              </h2>
              <nav className="space-y-1 mb-6">
                {priceRanges.map((range) => (
                  <button
                    key={range.id}
                    onClick={() => handlePriceRangeChange(range.id)}
                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                      filters.minPrice === undefined && filters.maxPrice === undefined && range.id === 'all'
                        ? 'bg-primary-50 text-primary-600 shadow-sm'
                        : filters.minPrice === 1000 && filters.maxPrice === 5000 && range.id === '1000-5000'
                        ? 'bg-primary-50 text-primary-600 shadow-sm'
                        : filters.minPrice === 5000 && filters.maxPrice === 10000 && range.id === '5000-10000'
                        ? 'bg-primary-50 text-primary-600 shadow-sm'
                        : filters.minPrice === 10000 && range.id === 'over-10000'
                        ? 'bg-primary-50 text-primary-600 shadow-sm'
                        : filters.maxPrice === 1000 && range.id === 'under-1000'
                        ? 'bg-primary-50 text-primary-600 shadow-sm'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {range.name}
                  </button>
                ))}
              </nav>

              <h2 className="text-lg font-bold bg-gradient-to-r from-primary-600 to-accent-500 bg-clip-text text-transparent mb-4">
                Revenue Status
              </h2>
              <nav className="space-y-1">
                {revenueStatuses.map((status) => (
                  <button
                    key={status.id}
                    onClick={() => handleRevenueStatusChange(status.id)}
                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                      filters.revenueStatus === undefined && status.id === 'all'
                        ? 'bg-primary-50 text-primary-600 shadow-sm'
                        : filters.revenueStatus === status.id
                        ? 'bg-primary-50 text-primary-600 shadow-sm'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {status.name}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Project Grid */}
          <div className="flex-1">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
                {error}
              </div>
            )}
            
            {loading ? (
              <div className="grid gap-6 animate-pulse">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white rounded-xl h-48"></div>
                ))}
              </div>
            ) : (
              <>
                {projects.length === 0 ? (
                  <div className="bg-white rounded-xl shadow-soft">
                    <EmptyState category={selectedCategory} />
                  </div>
                ) : (
                  <>
                    <div className={`grid gap-6 ${
                      view === 'grid'
                        ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                        : 'grid-cols-1'
                    }`}>
                      {projects.map((project) => (
                        <div key={project._id}>
                          <ProjectCard {...project} />
                        </div>
                      ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="mt-8 flex justify-center gap-2">
                        <Button
                          variant="secondary"
                          onClick={() => setFilters(prev => ({ ...prev, page: Math.max(1, prev.page! - 1) }))}
                          disabled={filters.page === 1}
                          className="px-4 py-2"
                        >
                          Previous
                        </Button>
                        <span className="px-4 py-2 text-sm text-gray-700">
                          Page {filters.page} of {totalPages}
                        </span>
                        <Button
                          variant="secondary"
                          onClick={() => setFilters(prev => ({ ...prev, page: Math.min(totalPages, prev.page! + 1) }))}
                          disabled={filters.page === totalPages}
                          className="px-4 py-2"
                        >
                          Next
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}; 